export type ContractUsageSource = 'manual' | 'katana';
export type ContractUsageMode = 'enforce' | 'report';
export type ContractUsageCapacityScope = 'route' | 'contract';

export type ContractUsageDraft = {
  rowId: number;
  enabled: boolean;
  source: ContractUsageSource;
  sourceKey?: string;
  sourceRecordCount?: number;
  sourceContractName?: string | null;
  sourceContractTspName?: string | null;
  sourceReceiptPointName?: string | null;
  sourceDeliveryPointName?: string | null;
  contractId: number | null;
  capacityScope: ContractUsageCapacityScope;
  receiptPointId: number | null;
  deliveryPointId: number | null;
  deliveryVolume: string;
};

export type ContractUsageWarning = {
  row_index: number;
  code: string;
  field: string | null;
  message: string;
  rowId?: number | null;
};

export type ContractUsageRequest = {
  contract_id: number | null;
  capacity_scope: ContractUsageCapacityScope;
  receipt_point_id: number | null;
  delivery_point_id: number | null;
  delivery_volume: string;
};

export type ContractUsageRequestFields = {
  contract_usages?: ContractUsageRequest[];
  contract_usage_mode?: ContractUsageMode;
};

export const contractUsageError = (
  usage: ContractUsageDraft,
): string | null => {
  if (!Number.isInteger(usage.contractId) || Number(usage.contractId) <= 0) {
    return 'select a contract';
  }
  if (usage.capacityScope === 'route') {
    if (
      !Number.isInteger(usage.receiptPointId) ||
      Number(usage.receiptPointId) <= 0
    ) {
      return 'select a receipt point';
    }
    if (
      !Number.isInteger(usage.deliveryPointId) ||
      Number(usage.deliveryPointId) <= 0
    ) {
      return 'select a delivery point';
    }
    if (usage.receiptPointId === usage.deliveryPointId) {
      return 'select a delivery point different from the receipt point';
    }
  }
  const volume = Number(usage.deliveryVolume);
  if (
    usage.deliveryVolume.trim() === '' ||
    !Number.isFinite(volume) ||
    volume <= 0
  ) {
    return 'enter an existing delivery volume greater than zero';
  }
  return null;
};

export const getEnabledContractUsages = (
  usages: ContractUsageDraft[],
): ContractUsageDraft[] => usages.filter((usage) => usage.enabled);

export const toContractUsageRequest = (
  usage: ContractUsageDraft,
): ContractUsageRequest => ({
  contract_id: usage.contractId,
  capacity_scope: usage.capacityScope,
  receipt_point_id:
    usage.capacityScope === 'route' ? usage.receiptPointId : null,
  delivery_point_id:
    usage.capacityScope === 'route' ? usage.deliveryPointId : null,
  delivery_volume: usage.deliveryVolume.trim(),
});

export const getContractUsageRequestFields = (
  usages: ContractUsageDraft[],
  mode: ContractUsageMode,
): ContractUsageRequestFields => {
  const enabledUsages = getEnabledContractUsages(usages);
  if (enabledUsages.length === 0) return {};

  return {
    contract_usages: enabledUsages.map(toContractUsageRequest),
    contract_usage_mode: mode,
  };
};

export const findInvalidEnabledContractUsageIndex = (
  usages: ContractUsageDraft[],
): number =>
  usages.findIndex(
    (usage) => usage.enabled && contractUsageError(usage) !== null,
  );

export const getContractUsageWarningsByRowId = (
  usages: ContractUsageDraft[],
  warnings: ContractUsageWarning[],
): Map<number, ContractUsageWarning[]> => {
  const enabledUsages = getEnabledContractUsages(usages);
  return warnings.reduce<Map<number, ContractUsageWarning[]>>(
    (lookup, warning) => {
      const rowId =
        warning.rowId ?? enabledUsages[warning.row_index]?.rowId ?? null;
      if (rowId == null) return lookup;
      const rowWarnings = lookup.get(rowId) || [];
      lookup.set(rowId, [...rowWarnings, warning]);
      return lookup;
    },
    new Map(),
  );
};
